import { useState } from "react"
import { Link, useNavigate } from "react-router"
import { toast } from "sonner"

import { useAplicaciones, useCrearAplicacion, usePuede } from "@/api/queries"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Skeleton } from "@/components/ui/skeleton"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { formatearCoste, formatearNumero } from "@/shared/format"

/**
 * El mismo patrón que exige el gateway: minúsculas, dígitos y guiones, empezando por
 * letra o dígito. Se comprueba aquí para poder explicarlo donde se escribe, no para
 * sustituir la validación del servidor — que sigue mandando.
 */
const SLUG = /^[a-z0-9][a-z0-9-]*$/

/**
 * De "FOODI Móvil" a "foodi-movil". Es una sugerencia editable, no una imposición: el
 * identificador entra en los informes de consumo y a veces se quiere distinto del
 * nombre comercial.
 */
function sugerirSlug(nombre: string): string {
  return nombre
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
}

export function AppsPage() {
  const apps = useAplicaciones(30)
  const crear = useCrearAplicacion()
  const navegar = useNavigate()
  //: Un `viewer` entra aquí a mirar el gasto. Enseñarle un botón que siempre le va a
  //: dar 403 le enseña a desconfiar de la interfaz. Esconderlo es cortesía, no
  //: seguridad: el permiso lo decide el servidor igual.
  const puedeCrear = usePuede("gestionar_apps")

  const [abierto, setAbierto] = useState(false)
  const [nombre, setNombre] = useState("")
  const [slug, setSlug] = useState("")
  //: Mientras nadie toque el identificador a mano, sigue al nombre. En cuanto se toca,
  //: deja de hacerlo: sobrescribir lo que alguien acaba de escribir es de las cosas que
  //: más molestan de un formulario.
  const [slugTocado, setSlugTocado] = useState(false)

  const slugFinal = slugTocado ? slug : sugerirSlug(nombre)
  const slugValido = SLUG.test(slugFinal)

  function abrir() {
    setNombre("")
    setSlug("")
    setSlugTocado(false)
    setAbierto(true)
  }

  async function enviar(e: React.FormEvent) {
    e.preventDefault()
    try {
      const creada = await crear.mutateAsync({ name: nombre.trim(), slug: slugFinal })
      setAbierto(false)
      toast.success("Aplicación creada", {
        description: "Ahora emitile una clave para que pueda llamar al gateway.",
      })
      // Directo al detalle, que es donde se emite la clave. Una aplicación sin clave no
      // puede hacer nada, así que dejar a alguien en la lista es dejarlo a mitad.
      void navegar(`/apps/${creada.app_id}`)
    } catch {
      toast.error("No se pudo crear la aplicación", {
        description: "¿Ya existe una con ese identificador?",
      })
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Aplicaciones</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Cada cliente que llama al gateway es una aplicación. El consumo y el coste se
            atribuyen a ella, y las claves le pertenecen.
          </p>
        </div>
        {puedeCrear && <Button onClick={abrir}>Nueva aplicación</Button>}
      </div>

      <Card>
        <CardContent className="pt-6">
          {apps.isPending ? (
            <Skeleton className="h-40" />
          ) : apps.isError ? (
            <p className="text-sm text-destructive">No se pudieron cargar las aplicaciones.</p>
          ) : apps.data.length === 0 ? (
            <div className="space-y-3 py-8 text-center">
              <p className="text-sm text-muted-foreground">
                Todavía no hay aplicaciones. Crear una es el primer paso para que algo pueda
                usar el gateway.
              </p>
              {puedeCrear && (
                <Button variant="outline" onClick={abrir}>
                  Crear la primera
                </Button>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Aplicación</TableHead>
                    <TableHead>Estado</TableHead>
                    <TableHead className="text-right">Claves</TableHead>
                    <TableHead className="text-right">Peticiones</TableHead>
                    <TableHead className="text-right">Coste (30 d)</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {apps.data.map((a) => (
                    <TableRow key={a.app_id}>
                      <TableCell>
                        <Link to={`/apps/${a.app_id}`} className="hover:underline">
                          <div className="font-medium">{a.name}</div>
                          <div className="text-xs text-muted-foreground">{a.slug}</div>
                        </Link>
                      </TableCell>
                      <TableCell>
                        <Badge variant={a.status === "active" ? "secondary" : "destructive"}>
                          {a.status === "active" ? "activa" : "suspendida"}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right tabular-nums">{a.claves_activas}</TableCell>
                      <TableCell className="text-right tabular-nums">
                        {formatearNumero(a.consumo.peticiones)}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {formatearCoste(a.consumo.cost_micros)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={abierto} onOpenChange={setAbierto}>
        <DialogContent>
          <form onSubmit={enviar}>
            <DialogHeader>
              <DialogTitle>Nueva aplicación</DialogTitle>
              <DialogDescription>
                Una por cliente que llame al gateway. Si dos cosas distintas comparten
                aplicación, su consumo se mezcla y ya no se puede separar después.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="app-nombre">Nombre</Label>
                <Input
                  id="app-nombre"
                  placeholder="FOODI"
                  value={nombre}
                  onChange={(e) => setNombre(e.target.value)}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="app-slug">Identificador</Label>
                <Input
                  id="app-slug"
                  placeholder="foodi"
                  value={slugFinal}
                  onChange={(e) => {
                    setSlugTocado(true)
                    setSlug(e.target.value)
                  }}
                  aria-invalid={slugFinal !== "" && !slugValido}
                  required
                />
                <p className="text-xs text-muted-foreground">
                  Minúsculas, números y guiones. Aparece en los informes de consumo y{" "}
                  <strong>no se puede cambiar después</strong>: el histórico ya facturado
                  quedaría a nombre de algo que no existe.
                </p>
                {slugFinal !== "" && !slugValido && (
                  <p className="text-xs text-destructive">
                    Solo minúsculas, números y guiones, empezando por letra o número.
                  </p>
                )}
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setAbierto(false)}>
                Cancelar
              </Button>
              <Button
                type="submit"
                disabled={crear.isPending || nombre.trim() === "" || !slugValido}
              >
                {crear.isPending ? "Creando…" : "Crear aplicación"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  )
}
